import React from 'react';
import { createGlobalStyle } from 'styled-components';

import { getFullFontFamily } from './theme.utils';

/**
 * Website Theme's own `colors`/`fonts` shape -- intentionally duplicated here rather than
 * imported from `src/cms/components/cms-editor-header/theme-colors.ts`/`theme-fonts.ts` (the CMS
 * editor's own copies of this same field list). `src/theme` is a lower-level module `src/cms`
 * already imports FROM (see e.g. `src/cms/components/cms-editor-header/theme-fonts.ts`) -- a
 * reverse import here would invert that layering. The field keys below are kept in lockstep BY
 * CONVENTION (same string literals, enforced by the type system structurally matching wherever a
 * `ThemeDraft`/`themeResponseToDraft` result is passed in as `theme`), not by a shared type.
 *
 * Scope is deliberately narrower than the full CMS editor's `ThemeDraft` type: only `colors` and
 * `fonts.primary`/`fonts.secondary` map onto an EXISTING real `--dcm-*` CSS custom property today
 * (see `core-menu-variables.ts`). `textTransforms`/`extensions`/`radius`/`productCardButton` have
 * no existing CSS-var application path -- text-transform in particular is a JS-computed style
 * prop (`theme.utils.getTextTransformStyle`), not a `var()`-overridable custom property, so
 * wiring it in requires changing how `theme/builders/styled.js` builds the styled-components
 * theme object, not just adding a CSS override here. That's an explicit, tracked follow-up, not
 * an oversight -- see PLAN.md.
 *
 * `fonts.*` stores a bare font key (e.g. `'Lato'`), not a CSS `font-family` stack -- this module
 * runs it through `getFullFontFamily` (same helper `ThemeProvider` already uses for the
 * dispensary-level path) before it ever reaches a declaration, both to add the real fallback
 * stack and because the Google Fonts `<link>` that actually loads the webfont bytes is built
 * from this same stored value (see `theme/provider/index.jsx`'s `encodedFontsRef` wiring) --
 * skipping that mapping here would silently regress the override to a font the browser never
 * fetched.
 */
export type WebsiteThemeColors = Partial<
  Record<
    | 'bodyBackground'
    | 'border'
    | 'borderDark'
    | 'buttonsLinks'
    | 'discountTag'
    | 'focus'
    | 'focusShadow'
    | 'navBar'
    | 'staffPickTag'
    | 'surface'
    | 'surfaceHover'
    | 'surfaceMuted'
    | 'textInverted'
    | 'textMuted'
    | 'textPrimary'
    | 'textSecondary',
    string
  >
>;

export type WebsiteThemeFonts = Partial<Record<'primary' | 'secondary', string>>;

export type WebsiteTheme = {
  colors?: WebsiteThemeColors;
  fonts?: WebsiteThemeFonts;
};

/**
 * `colors.<field>` -> the real `--dcm-color-*` custom property it overrides on `:root` (see
 * `core-menu-variables.ts`'s "GLOBAL — Colors: Core Palette" block -- every entry here is a
 * direct, confirmed match, not a guess). Several real var names do NOT follow a mechanical
 * camelCase-to-kebab-case transform of the field name (`buttonsLinks` -> `--dcm-color-primary`,
 * not `--dcm-color-buttons-links`), which is exactly why this is an explicit map rather than a
 * computed kebab-case helper like the CMS editor's own preview-only
 * `theme-colors.ts#themeColorPreviewVar`.
 */
const COLOR_CSS_VAR_BY_FIELD: Record<keyof WebsiteThemeColors, string> = {
  buttonsLinks: '--dcm-color-primary',
  navBar: '--dcm-color-navbar-bg',
  staffPickTag: '--dcm-color-staff-pick-tag',
  discountTag: '--dcm-color-discount-tag',
  bodyBackground: '--dcm-color-body-bg',
  surface: '--dcm-color-surface',
  surfaceHover: '--dcm-color-surface-hover',
  surfaceMuted: '--dcm-color-surface-muted',
  textPrimary: '--dcm-color-text-primary',
  textSecondary: '--dcm-color-text-secondary',
  textInverted: '--dcm-color-text-inverted',
  textMuted: '--dcm-color-text-muted',
  border: '--dcm-color-border',
  borderDark: '--dcm-color-border-dark',
  focus: '--dcm-color-focus',
  focusShadow: '--dcm-color-focus-shadow',
};

/**
 * `bodyBackground` ALSO overrides `--dcm-cms-page-bg` -- the established CMS page-root theme
 * hook (`page-root-render.tsx`, added by ECOMM-13909 for the Ecommerce Pro SDK/legacy remote
 * theme system; see that component's own doc comment) that a standalone/menu-landing page's
 * "Page background" field falls through to whenever an author leaves it at its default Surface
 * role. Found live: an author who never set a per-page background override, expecting their
 * Website Theme's own "Page background" field to apply, saw the page stay plain white, because
 * nothing had ever written to this hook. Per PLAN.md's locked cascade order, Website Theme is
 * injected last and is authoritative over legacy hand-authored remote theming, so `bodyBackground`
 * winning here (same as every other Website Theme field already wins over the SDK/legacy system
 * it overrides) is the correct, not merely convenient, precedence -- not a second, competing
 * source of truth for "the page background".
 */
const CMS_PAGE_BACKGROUND_CSS_VAR = '--dcm-cms-page-bg';

/**
 * Loose but intentional allowlist for a stored color value before it's interpolated into a
 * global `:root { ... }` block verbatim. `theme.colors.*` round-trips through this module's own
 * GraphQL query as a plain `String` with no server-side validator in this repo (the CMS worker's
 * theme route doesn't constrain it either) -- a merchant/enterprise-admin-authored value like
 * `#fff } body { display:none } :root {` would otherwise close this rule early and inject
 * arbitrary CSS (or `url(...)` beacons) into every visitor's document. Hex (3/4/6/8-digit, for
 * the `focusShadow` alpha case) and `rgb()`/`rgba()` cover every color format the CMS editor's
 * own color picker can produce -- anything else is dropped the same way an unset field is,
 * rather than rendered. Write access to this value is limited to enterprise admins, which bounds
 * the blast radius, but this check is cheap enough that "limited blast radius" isn't a reason to
 * skip it.
 */
const CSS_COLOR_VALUE_REGEX = /^(?:#[0-9a-fA-F]{3,4}|#[0-9a-fA-F]{6}|#[0-9a-fA-F]{8}|rgba?\(\s*\d{1,3}%?\s*,\s*\d{1,3}%?\s*,\s*\d{1,3}%?\s*(?:,\s*(?:0|1|0?\.\d+)\s*)?\))$/;

function isValidCssColorValue(value: string): boolean {
  return CSS_COLOR_VALUE_REGEX.test(value.trim());
}

/**
 * Type-guard narrowing helper for the `[cssVar, value]` tuples built below -- lets `.filter()`
 * narrow `value` from `string | undefined` to `string` for the rest of the chain, instead of a
 * forbidden `!`/`as string` assertion at each of the two (color, font) call sites.
 */
function hasSetValue<K>(entry: readonly [K, string | undefined]): entry is readonly [K, string] {
  return Boolean(entry[1]);
}

/** `fonts.primary`/`fonts.secondary` -> the real `--dcm-font-*` custom property (see `core-menu-variables.ts`'s "GLOBAL — Typography" block). */
const FONT_CSS_VAR_BY_FIELD: Record<keyof WebsiteThemeFonts, string> = {
  primary: '--dcm-font-primary',
  secondary: '--dcm-font-secondary',
};

/**
 * Builds the `:root { --dcm-...: ...; }` declaration block for whatever fields `theme` actually
 * sets -- an unset field emits NO declaration for its var at all, so it falls through to
 * whatever's beneath this override in the cascade (dispensary-level `webCustomizationSettingsV2`,
 * then the platform default), exactly like the sparse "unset = fall through" contract
 * `ThemeDraft`/the saved REST payload already use one level up. Never emits a declaration for a
 * field this module doesn't know about (not just an empty string) -- only HERE and in
 * `COLOR_CSS_VAR_BY_FIELD`/`FONT_CSS_VAR_BY_FIELD` above is that field list allowed to grow.
 *
 * Exported (not just used internally by `WebsiteThemeVariables` below) so other consumers that
 * need these same declarations OUTSIDE this component's own `:root`-on-the-host-document write
 * -- e.g. the CMS editor's canvas-iframe bridge, which must write them into a SEPARATE document
 * (Puck's preview iframe) that this component never touches -- can build the identical text
 * without duplicating the field-to-var maps above. Pass `important: true` to suffix every
 * declaration with `!important`, needed by that same canvas bridge to win the cascade against
 * whatever Puck's own (separate, best-effort) host-style mirroring copies into that iframe --
 * see that bridge's own doc comment for why `:root`-vs-`:root` source order can't be trusted.
 */
export function buildWebsiteThemeDeclarations(
  theme: WebsiteTheme | null | undefined,
  { important = false }: { important?: boolean } = {}
): string {
  const suffix = important ? ' !important;' : ';';

  const colorLines = (Object.entries(COLOR_CSS_VAR_BY_FIELD) as [keyof WebsiteThemeColors, string][])
    .map(([field, cssVar]) => [cssVar, theme?.colors?.[field]] as const)
    .filter(hasSetValue)
    .filter(([, value]) => isValidCssColorValue(value))
    .map(([cssVar, value]) => `${cssVar}: ${value}${suffix}`);

  // Mapped through `getFullFontFamily` (same helper the dispensary-level `webCustomizationSettingsV2`
  // path already uses in `ThemeProvider`) rather than emitted as the bare stored font key --
  // without it, this var would win the cascade with a font name the browser never actually loaded
  // (no webfont `<link>`/no CSS fallback stack), silently regressing every overridden font to the
  // browser default. `getFullFontFamily` also doubles as this field's allowlist: it falls back to
  // MATTER for any value that isn't a real `fontsV2` key, so an arbitrary string can't reach this
  // declaration either -- see `isValidCssColorValue`'s own doc comment for the matching concern on
  // the color side.
  const fontLines = (Object.entries(FONT_CSS_VAR_BY_FIELD) as [keyof WebsiteThemeFonts, string][])
    .map(([field, cssVar]) => [cssVar, theme?.fonts?.[field]] as const)
    .filter(hasSetValue)
    .map(([cssVar, value]) => `${cssVar}: ${String(getFullFontFamily(value))}${suffix}`);

  // See CMS_PAGE_BACKGROUND_CSS_VAR's own doc comment -- a second declaration for the same
  // `bodyBackground` value, not a second field, so it has no separate "is this field set" check
  // (beyond the same color-value allowlist every other color field above goes through).
  const pageBackgroundLine =
    theme?.colors?.bodyBackground && isValidCssColorValue(theme.colors.bodyBackground)
      ? [`${CMS_PAGE_BACKGROUND_CSS_VAR}: ${theme.colors.bodyBackground}${suffix}`]
      : [];

  return [...colorLines, ...pageBackgroundLine, ...fontLines].join('\n  ');
}

const StyleBlock = createGlobalStyle<{ $declarations: string }>`
  :root {
    ${({ $declarations }) => $declarations}
  }
`;

/**
 * Applies Website Theme's color/font overrides to the document, as plain `--dcm-*` custom
 * property overrides on `:root`. Renders nothing (not even an empty `<style>` tag) when `theme`
 * has no fields set at all -- a merchant/page with no Website Theme configured should leave the
 * cascade completely untouched by this component's own existence.
 *
 * MUST be mounted after `CoreMenuVariables` and any remote SDK `GlobalStyles` module in DOM
 * order, to be authoritative over both per PLAN.md's locked cascade order ("Website theme is
 * injected last... becomes authoritative over legacy hand-authored remote theming"). `:root`-vs-
 * `:root` is equal specificity, so DOM/mount order is genuinely what decides precedence here --
 * see `theme/provider/index.jsx`'s own render order for where this is actually mounted.
 */
export function WebsiteThemeVariables({ theme }: { theme: WebsiteTheme | null | undefined }): JSX.Element | null {
  const declarations = buildWebsiteThemeDeclarations(theme);
  if (!declarations) return null;
  return <StyleBlock $declarations={declarations} />;
}
